import styles from "../../styles/CoordinationBoards.module.css"
import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/userContext"
import axios from "axios"
import { coordinationBoardsApiUrl } from "../../config/microservicesUrls"
import { Actions, TagType, TopicStatus } from "./Types/Enums"
import { topicStatusOptions } from "./Types/TypeUtils"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { RadioButton } from "primereact/radiobutton"
import { AutoComplete } from "primereact/autocomplete"
import { showError, requiredFieldIsMissing, getMentions } from "./Utils/Utils"
import EditorField from "./Utils/EditorField"
import TextField from "./Utils/TextField"
import { Tag } from "primereact/tag"

const api_url = coordinationBoardsApiUrl()

const TopicForm = ({
  dialogVisible,
  setDialogVisible,
  board,
  defaultTags,
  groupedParticipants,
  addTopic,
  updateTopic,
  action,
  topic,
  toast,
  justStatusEdits,
  staffSuggestions,
}) => {
  const userCtx = useContext(UserContext)
  const defaultTopicData = {
    boardId: board.id,
    subject: "",
    messageText: "",
    status: TopicStatus.Active,
    updateReason: undefined,
  }
  const [topicData, setTopicData] = useState(defaultTopicData)
  const [prevMessageText, setPrevMessageText] = useState("")
  const [loading, setLoading] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedTag, setSelectedTag] = useState(null)
  const [filteredTags, setFilteredTags] = useState(null)
  const [selectedParticipantTags, setSelectedParticipantTags] = useState([])
  const [selectedParticipantTag, setSelectedParticipantTag] = useState(null)
  const [filteredParticipantTags, setFilteredParticipantTags] = useState(null)
  const allowParticipantTag = true
  const tags = defaultTags.slice(0, -1)
  const participantTag = defaultTags[defaultTags.length - 1]

  const [showRequiredFieldMge, setShowRequiredFieldMge] = useState({
    subject: false,
    messageText: false,
    updateReason: false,
  })

  useEffect(() => {
    if (dialogVisible && topic != null && action !== Actions.Add) {
      const _topic = {
        id: topic.id,
        boardId: topic.boardId,
        subject: topic.subject,
        messageText: topic.messageText,
        status: topic.status,
      }
      let _selectedTags = []
      let _selectedParticipantTags = []
      if (topic.tags.length > 0) {
        _selectedTags = topic.tags.filter((tag) => tag.type === TagType.Default)
        _selectedParticipantTags = topic.tags.filter(
          (tag) => tag.type === TagType["Default Participant"],
        )
        _selectedParticipantTags = _selectedParticipantTags.map((tag) => {
          const metadata = JSON.parse(tag.metadata)
          return {
            ...metadata,
            label: `${metadata.name} - ID ${metadata.alias}`,
          }
        })
      }
      setSelectedTags(_selectedTags)
      setSelectedParticipantTags(_selectedParticipantTags)
      setTopicData(_topic)
      setPrevMessageText(topic.messageText)
    } else {
      clearForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible])

  const clearForm = () => {
    setTopicData(defaultTopicData)
    setPrevMessageText("")
    setSelectedTags([])
    setSelectedTag(null)
    setFilteredTags(null)
    setSelectedParticipantTags([])
    setSelectedParticipantTag(null)
    setFilteredParticipantTags(null)
    setLoading(false)
    setShowRequiredFieldMge({
      subject: false,
      messageText: false,
      updateReason: false,
    })
  }

  const tagSearch = (event) => {
    let _filteredTags
    if (!event.query.trim().length) {
      // set filtered tags to all tags excluding the already selected ones if any
      _filteredTags = selectedTags
        ? tags.filter((tag) => {
            return !selectedTags.some(
              (selectedTag) => selectedTag.tagId === tag.tagId,
            )
          })
        : tags
    } else {
      // set filtered tags to the ones that start with the query excluding the already selected ones if any
      _filteredTags = selectedTags
        ? tags.filter((tag) => {
            return (
              tag.name.toLowerCase().startsWith(event.query.toLowerCase()) &&
              !selectedTags.some(
                (selectedTag) => selectedTag.tagId === tag.tagId,
              )
            )
          })
        : tags.filter((tag) => {
            return tag.name.toLowerCase().startsWith(event.query.toLowerCase())
          })
    }
    setFilteredTags(_filteredTags)
  }

  const participantTagSearch = (event) => {
    let _filteredTags = []
    if (!event.query.trim().length) {
      // set filtered participants to all participants excluding the already selected ones if any
      groupedParticipants.forEach((group) => {
        let filteredParticipants = []
        group.participants.forEach((participant) => {
          if (
            !selectedParticipantTags ||
            !selectedParticipantTags.some((sel) => sel.id === participant.id)
          ) {
            filteredParticipants.push(participant)
          }
        })
        filteredParticipants.sort((a, b) => a.name.localeCompare(b.name))
        _filteredTags.push({
          orgName: group.orgName,
          participants: filteredParticipants,
        })
      })
    } else {
      // set filtered participants to the ones that start with the query excluding the already selected ones if any
      groupedParticipants.forEach((group) => {
        let filteredParticipants = []
        group.participants.forEach((participant) => {
          if (
            participant.name
              .toLowerCase()
              .startsWith(event.query.toLowerCase()) &&
            (!selectedParticipantTags ||
              !selectedParticipantTags.some((sel) => sel.id === participant.id))
          ) {
            filteredParticipants.push(participant)
          }
        })
        //sort filteredParticipants by name
        filteredParticipants.sort((a, b) => a.name.localeCompare(b.name))
        _filteredTags.push({
          orgName: group.orgName,
          participants: filteredParticipants,
        })
      })
    }
    setFilteredParticipantTags(_filteredTags)
  }

  const save = () => {
    const _tags = selectedTags
      ? selectedTags.map((tag) => {
          return { tagId: tag.tagId, metadata: "" }
        })
      : []
    const _participantTags = selectedParticipantTags
      ? selectedParticipantTags.map((p) => {
          return {
            tagId: participantTag.tagId,
            metadata: `{"id":"${p.id}","alias":${p.alias},"name":"${p.name}"}`,
          }
        })
      : []

    setShowRequiredFieldMge({
      subject: true,
      messageText: true,
      updateReason: true,
    })

    let data = {
      ...topicData,
      tags: [_tags, _participantTags].flat(),
    }

    if (action === Actions.Add) {
      if (
        requiredFieldIsMissing(data.subject) ||
        requiredFieldIsMissing(data.messageText)
      ) {
        return
      }
      setLoading(true)
      data = {
        ...data,
        mentions: getMentions(topicData.messageText, staffSuggestions),
        createdBy: userCtx.session.user.staff_id,
        createdByOrganization: userCtx.session.user.organization_id,
      }
      axios
        .post(`${api_url}/topics`, data, {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
          },
        })
        .then(function (response) {
          setLoading(false)
          addTopic(response.data)
          clearForm()
          setDialogVisible(false)
        })
        .catch(function (response) {
          setLoading(false)
          const errors = response["response"]["data"]["errors"]
          if (response["response"]["status"] === 400) {
            showError(toast, errors, errors)
          } else {
            showError(toast, "Not able to create discussion", errors)
          }
        })
    }

    if (action === Actions.Edit) {
      if (
        requiredFieldIsMissing(data.subject) ||
        requiredFieldIsMissing(data.messageText) ||
        requiredFieldIsMissing(data.updateReason)
      ) {
        return
      }
      setLoading(true)
      data = {
        ...data,
        newMentions: getMentions(topicData.messageText, staffSuggestions),
        oldMentions: getMentions(prevMessageText, staffSuggestions),
        updatedBy: userCtx.session.user.staff_id,
        updatedByOrganization: userCtx.session.user.organization_id,
      }
      axios
        .put(`${api_url}/topics`, data, {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
          },
        })
        .then(function (response) {
          setLoading(false)
          updateTopic(response.data)
          clearForm()
          setDialogVisible(false)
        })
        .catch(function (response) {
          setLoading(false)
          const errors = response["response"]["data"]["errors"]
          if (response["response"]["status"] === 400) {
            showError(toast, errors, errors)
          } else {
            showError(toast, "Not able to update discussion", errors)
          }
        })
    }
  }

  const dialogTitle = () => {
    switch (action) {
      case Actions.Add:
        return "New Discussion"
      case Actions.Edit:
        return "Edit Discussion"
      default:
        return ""
    }
  }

  const saveBtnTxt = () => {
    switch (action) {
      case Actions.Add:
        return "Add"
      case Actions.Edit:
        return "Save"
      default:
        return ""
    }
  }

  return (
    <Dialog
      header={dialogTitle}
      className={`custom_dialog ${styles.topic_dialog}`}
      visible={dialogVisible}
      onHide={() => {
        clearForm()
        setDialogVisible(false)
      }}
    >
      <div
        style={{
          padding: "10px",
        }}
      >
        {!justStatusEdits && (
          <TextField
            value={topicData.subject}
            label={"Subject"}
            onChange={(e) => {
              setShowRequiredFieldMge({
                ...showRequiredFieldMge,
                subject: false,
              })
              setTopicData({
                ...topicData,
                subject: e.target.value,
              })
            }}
            required={true}
            valueName={"subject"}
            showRequiredFieldMge={showRequiredFieldMge.subject}
          />
        )}
        {!justStatusEdits && (
          <EditorField
            value={topicData.messageText}
            onTextChange={(e) => {
              setShowRequiredFieldMge({
                ...showRequiredFieldMge,
                messageText: false,
              })
              setTopicData({ ...topicData, messageText: e })
            }}
            required={true}
            label="Description"
            placeholder="Enter @ to mention people"
            showRequiredFieldMge={showRequiredFieldMge.messageText}
            suggestions={staffSuggestions}
          />
        )}
        {action === Actions.Edit && (
          <TextField
            value={topicData.updateReason}
            label={"Reason"}
            onChange={(e) => {
              setShowRequiredFieldMge({
                ...showRequiredFieldMge,
                updateReason: false,
              })
              setTopicData({
                ...topicData,
                updateReason: e.target.value,
              })
            }}
            required={true}
            valueName={"updateReason"}
            showRequiredFieldMge={showRequiredFieldMge.updateReason}
          />
        )}
        {!justStatusEdits && (
          <div className={styles.field}>
            <label className={styles.label}>Tags</label>
            <AutoComplete
              style={{ width: "100%" }}
              field="name"
              value={selectedTag}
              suggestions={filteredTags}
              completeMethod={tagSearch}
              dropdown
              onChange={(e) => {
                const selectedValue = e.value
                if (selectedValue && selectedValue.tagId) {
                  setSelectedTags([...selectedTags, selectedValue])
                  setSelectedTag(null)
                } else {
                  setSelectedTag(selectedValue)
                }
              }}
            />
            {selectedTags && selectedTags.length > 0 && (
              <div
                className={styles.tag_container}
                style={{ marginTop: "10px", marginBottom: "30px" }}
              >
                {selectedTags.map((tag) => {
                  return (
                    <Tag key={tag.tagId}>
                      <div className={styles.form_tag}>
                        <span>{tag.name}</span>
                        <Button
                          icon="pi pi-times-circle"
                          className={`${styles.rounded_btn}`}
                          onClick={() => {
                            //remove tag from selected tags
                            const _selectedTags = selectedTags.filter(
                              (t) => t.tagId !== tag.tagId,
                            )
                            setSelectedTags(_selectedTags)
                          }}
                        />
                      </div>
                    </Tag>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {!justStatusEdits && allowParticipantTag && (
          <div className={styles.field}>
            <label className={styles.label}>Participants</label>
            <AutoComplete
              style={{ width: "100%" }}
              panelStyle={{ fontSize: "13px" }}
              field="label"
              optionGroupChildren="participants"
              optionGroupLabel="orgName"
              value={selectedParticipantTag}
              dropdown
              suggestions={filteredParticipantTags}
              completeMethod={participantTagSearch}
              onChange={(e) => {
                const selectedValue = e.value
                if (selectedValue && selectedValue.id) {
                  setSelectedParticipantTags([
                    ...selectedParticipantTags,
                    selectedValue,
                  ])
                  setSelectedParticipantTag(null)
                } else {
                  setSelectedParticipantTag(selectedValue)
                }
              }}
            />
            {selectedParticipantTags && selectedParticipantTags.length > 0 && (
              <div
                className={styles.tag_container}
                style={{ marginTop: "10px", marginBottom: "30px" }}
              >
                {selectedParticipantTags.map((pTag) => {
                  return (
                    <Tag key={pTag.id}>
                      <div className={styles.form_tag}>
                        <span>{pTag.label}</span>
                        <Button
                          icon="pi pi-times-circle"
                          className={`${styles.rounded_btn}`}
                          onClick={() => {
                            //remove tag from selected tags
                            const _selectedParticipantTags =
                              selectedParticipantTags.filter(
                                (p) => p.id !== pTag.id,
                              )
                            setSelectedParticipantTags(_selectedParticipantTags)
                          }}
                        />
                      </div>
                    </Tag>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {action === Actions.Edit && (
          <div className={styles.field}>
            <label className={styles.label} style={{ marginBottom: "5px" }}>
              Status
            </label>
            <div className={styles.radio_btn_wrapper}>
              {topicStatusOptions().map((status) => {
                return (
                  <div key={`status${status.id}`} className={styles.radio_btn}>
                    <RadioButton
                      inputId={status.id}
                      value={status.value}
                      style={{ margin: "0" }}
                      onChange={(e) => {
                        setTopicData({
                          ...topicData,
                          status: e.value,
                        })
                      }}
                      checked={topicData.status === status.value}
                    />
                    <label htmlFor={status.id} className={styles.inline_label}>
                      {status.label}
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: "20px",
            justifyContent: "flex-end",
          }}
        >
          <Button
            className={styles.cancel_button}
            type="submit"
            disabled={loading}
            onClick={() => {
              clearForm()
              setDialogVisible(false)
            }}
          >
            Cancel
          </Button>
          <Button
            className={styles.button}
            type="submit"
            style={{
              width: "91px!important",
              justifyContent: "center",
            }}
            disabled={loading}
            onClick={() => save()}
          >
            {saveBtnTxt()}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
export default TopicForm
